import { StarIcon } from "@heroicons/react/24/outline"
import { useNavigate } from "react-router-dom"
import type { KeyEventDto } from "~/ui-rtk/api/keyEventsApi"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Button } from "~/ui-rtk/components/ui/controls"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import useDate from "~/ui-rtk/hooks/date"

function Detail({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-system-gray-500">
        {label}
      </div>
      <div className="text-sm text-system-gray-200">{value}</div>
    </div>
  )
}

/**
 * Read-only view of a company key event shown from the agenda calendar —
 * key events are managed on the Settings page, the agenda only overlays them
 * so planning happens around launches/promos.
 */
export default function KeyEventDialog({
  keyEvent,
  onClose,
}: {
  keyEvent: KeyEventDto | null
  onClose: () => void
}) {
  const dayjs = useDate()
  const navigate = useNavigate()

  return (
    <Dialog
      isOpen={Boolean(keyEvent)}
      handleClose={onClose}
      width={420}
      closeOnClickOutside
      classes={{
        container:
          "bg-basic-black border border-system-blue-600/40 p-6 rounded-xl",
      }}
    >
      {keyEvent && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500/20">
              <StarIcon className="h-4 w-4 text-amber-400" />
            </span>
            <div>
              <h3 className="text-lg font-semibold text-white">
                {keyEvent.name}
              </h3>
              <p className="text-xs text-system-gray-500">Company key event</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Detail label="Type" value={keyEvent.type} />
            <Detail
              label="Date"
              value={
                keyEvent.end &&
                !dayjs(keyEvent.end).isSame(keyEvent.start, "day")
                  ? `${dayjs(keyEvent.start).format("MMM D, YYYY")} – ${dayjs(keyEvent.end).format("MMM D, YYYY")}`
                  : dayjs(keyEvent.start).format("MMM D, YYYY")
              }
            />
            <Detail
              label="Planned spend"
              value={
                keyEvent.spend != null
                  ? `$${keyEvent.spend.toLocaleString()}`
                  : undefined
              }
            />
          </div>

          <div className="flex justify-end gap-2 border-t border-system-blue-600/30 pt-4">
            <Button
              variant={{ variant: "text" }}
              size="sm"
              type="button"
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              variant={{ variant: "solid", color: "blue" }}
              size="sm"
              type="button"
              onClick={() => navigate(brandRoutes.SETTINGS.KEY_EVENTS)}
            >
              Manage key events
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  )
}
